import React, { useContext } from "react";
import { columnLeft, columnsWidth, PlanGrid } from "modules/spending/workload-plan/models/plan-scale";
import { CONFLICT_KIND_LABELS, ConflictRange, ConflictTarget } from "../models/plan-conflicts";
import styles from "./PlanTrack.module.scss";

/** Классы полос — общие для строки участника и для свёрнутой строки проекта */
export const trackStyles = styles;

interface PlanTrackProps {
    grid: PlanGrid;
    /** Левые края разделителей месяцев, px */
    monthBreaks: number[];
    /** Слот, выделенный кликом по шапке */
    selectedSlot: { start: number; length: number } | null;
    /** Дополнительный класс: разделитель у строки участника и у строки проекта разный */
    className?: string;
    /** Дни конфликтов в этой строке — красная штриховка поверх полос */
    conflicts?: ConflictRange[];
    children?: React.ReactNode;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Дорожка сетки: ячейки, прошлое, текущий день, пустые недели и границы месяцев.
 * Одна на все строки страницы — иначе шапка, строки проектов и строки участников
 * разъезжаются между собой при первой же правке фона.
 */
export const PlanTrack: React.FC<PlanTrackProps> = React.memo((props) => {
    const { grid, monthBreaks, selectedSlot, className, conflicts, children, onDoubleClick, onContextMenu } = props;

    // прошлое приглушается до начала текущего слота — неделя целиком не «прошлая»
    const firstFuture = grid.slots.find((slot) => !slot.isPast);
    const pastWidth = firstFuture ? firstFuture.left : grid.totalWidth;
    const today = grid.slots.find((slot) => slot.isToday) || null;

    return (
        <div
            className={className ? `${styles.track} ${className}` : styles.track}
            data-plan-track={true}
            style={
                {
                    width: grid.totalWidth,
                    // полоски фона идут по границам слотов — как колонки в шапке
                    "--wp-grid-step": `${grid.slotWidth}px`,
                } as React.CSSProperties
            }
            onDoubleClick={onDoubleClick}
            onContextMenu={onContextMenu}
        >
            {pastWidth > 0 && <div className={styles.past} style={{ width: pastWidth }} />}

            {today && <div className={styles.today} style={{ left: today.left, width: today.width }} />}

            {selectedSlot && (
                <div
                    className={styles.columnMark}
                    style={{
                        left: columnLeft(grid, selectedSlot.start),
                        width: columnsWidth(grid, selectedSlot.start, selectedSlot.length),
                    }}
                />
            )}

            {grid.slots.map((slot) =>
                slot.isEmpty ? (
                    <div
                        key={`empty_${slot.left}`}
                        className={styles.emptyWeek}
                        style={{ left: slot.left, width: slot.width }}
                        data-tip="Рабочих дней на этой неделе нет"
                    />
                ) : null
            )}

            {monthBreaks.map((left) => (
                <div key={left} className={styles.monthBreak} style={{ left }} />
            ))}

            {children}

            {/* поверх полос, но под подписью: номер проекта под штриховкой читается */}
            {conflicts &&
                conflicts.map((range) => (
                    <div
                        key={`${range.kind}_${range.start}`}
                        className={styles.conflict}
                        style={{
                            left: columnLeft(grid, range.start),
                            width: columnsWidth(grid, range.start, range.length),
                        }}
                    />
                ))}
        </div>
    );
});

PlanTrack.displayName = "PlanTrack";

/**
 * Переход ко второй стороне конфликта (двойной клик по его дням). Контекстом, а не пропсом:
 * зоны рисуют строки трёх уровней — проекта, раздела, сотрудника
 */
export const ConflictJumpContext = React.createContext<((target: ConflictTarget) => void) | null>(null);

interface ConflictZonesProps {
    grid: PlanGrid;
    /** Колонки самого блока — зоны кладутся только на его дни */
    start: number;
    length: number;
    conflicts?: ConflictRange[];
}

/**
 * Прозрачные зоны внутри блока на днях конфликта — у каждой своя подсказка, в чём
 * проблема. Штриховка поверх полос мышь не ловит (иначе полосу не взять), поэтому
 * подсказку несут дети самого блока: нажатие с них всплывает к блоку и тянет его как
 * обычно, а ручки краёв лежат выше зон. Координаты — от левого края блока. Двойной клик
 * по зоне ведёт к тому, с чем конфликт: к другой работе дня или к предшественнику связи.
 */
export const ConflictZones: React.FC<ConflictZonesProps> = ({ grid, start, length, conflicts }) => {
    const jump = useContext(ConflictJumpContext);

    if (!conflicts || !conflicts.length) {
        return null;
    }

    const blockLeft = columnLeft(grid, start);
    const end = start + length;

    return (
        <>
            {conflicts.map((range) => {
                const from = Math.max(start, range.start);
                const to = Math.min(end, range.start + range.length);

                if (from >= to) {
                    return null;
                }

                const target = jump && range.targets.length ? range.targets[0] : null;
                const hint = target
                    ? `Двойной клик — перейти к ${range.kind === "link" ? "предшественнику" : "работе"}${
                          target.label ? ` ${target.label}` : ""
                      }`
                    : "";

                return (
                    <span
                        key={`${range.kind}_${range.start}`}
                        className={styles.conflictZone}
                        // блок в рамке 1px: зона отсчитывается от его внутреннего края
                        style={{
                            left: columnLeft(grid, from) - blockLeft - 1,
                            width: columnsWidth(grid, from, to - from),
                        }}
                        data-tip={`Конфликт: ${CONFLICT_KIND_LABELS[range.kind].toLowerCase()}`}
                        data-tip-note={[...range.notes, hint].filter(Boolean).join("\n")}
                        onDoubleClick={
                            target
                                ? (event) => {
                                      // у полосы свой двойной клик (новая веха, окно работы)
                                      event.stopPropagation();
                                      event.preventDefault();
                                      jump!(target);
                                  }
                                : undefined
                        }
                    />
                );
            })}
        </>
    );
};
